import React from "react";

import { useTheme } from "@mui/material";

import GradientRange from "@app/analytics/components/GradientRange/GradientRange";
import COACHING_BENCHMARKS from "@app/common/coaching/coachingBenchmarks";

export const FillerWordsGradientRange = ({
  rate,
  withMarkerLabel = false,
}: {
  rate: number | null;
  withMarkerLabel?: boolean;
}) => {
  const theme = useTheme();
  const safeRate = rate ?? 0;

  // somewhat arbitrary threshold
  const division = safeRate < 20 ? 5 : 10;

  // round the max up to the nearest division and bump to next bucket if % lands right at the upper edge
  const maximumFillerWords = Math.min(
    Math.max(Math.ceil((safeRate + 1) / division) * division, division),
    100
  );

  const roundedPercent = Math.round(safeRate);

  return (
    <GradientRange
      gradient={[
        { color: theme.palette.shades.meadow[500], percent: "0%" },
        {
          color: theme.palette.shades.orange[400],
          percent: `${Math.max(
            COACHING_BENCHMARKS.fillerWordsThresholds.positive,
            (100 / maximumFillerWords) *
              COACHING_BENCHMARKS.fillerWordsThresholds.positive
          )}%`,
        },
        {
          color: "#FF4242",
          percent: `${Math.max(
            COACHING_BENCHMARKS.fillerWordsThresholds.neutral,
            (100 / maximumFillerWords) *
              COACHING_BENCHMARKS.fillerWordsThresholds.neutral
          )}%`,
        },
      ]}
      marker={
        rate != null
          ? {
              value: rate,
              label: withMarkerLabel ? `${roundedPercent}%` : undefined,
            }
          : undefined
      }
      range={[0, maximumFillerWords]}
      rangeLabels={["0%", `${maximumFillerWords}%`]}
    />
  );
};

export const NonInclusiveTermsGradientRange = ({
  count,
  withMarkerLabel = false,
}: {
  count: number | null;
  withMarkerLabel?: boolean;
}) => {
  const theme = useTheme();
  const safeCount = count ?? 0;

  // somewhat arbitrary threshold
  const division = safeCount < 20 ? 5 : 10;

  // round the max up to the nearest division and bump to next bucket if % lands right at the upper edge
  const rangeMax = Math.min(
    Math.max(Math.ceil((safeCount + 1) / division) * division, division),
    100
  );

  return (
    <GradientRange
      gradient={[
        { color: theme.palette.shades.meadow[500], percent: "0%" },
        {
          // TODO: base these %s on benchmark values
          color: theme.palette.shades.orange[400],
          percent: `${Math.max(3, (10000 / rangeMax) * 0.03)}%`,
        },
        {
          color: "#FF4242",
          percent: `${Math.max(5, (10000 / rangeMax) * 0.05)}%`,
        },
      ]}
      marker={
        count != null
          ? { value: count, label: withMarkerLabel ? `${count}` : undefined }
          : undefined
      }
      range={[0, rangeMax]}
    />
  );
};

export const InterruptionsGradientRange = NonInclusiveTermsGradientRange;

export const MetricGradientRange = ({
  value,
  withMarkerLabel = false,
}: {
  value: number | null;
  withMarkerLabel?: boolean;
}) => {
  const theme = useTheme();

  return (
    <GradientRange
      gradient={[
        { color: "#FF4242", percent: "0%" },
        { color: "#F2B02F", percent: "22%" },
        { color: theme.palette.shades.meadow[500], percent: "100%" },
      ]}
      marker={
        value
          ? { value, label: withMarkerLabel ? `${value}` : undefined }
          : undefined
      }
      range={[50, 100]}
    />
  );
};

export const QuestionsAskedGradientRange = ({
  count,
}: {
  count: number | null;
}) => {
  const theme = useTheme();
  const safeCount = count ?? 0;

  // somewhat arbitrary threshold
  const division = safeCount < 20 ? 5 : 10;

  // round the max up to the nearest division and bump to next bucket if % lands right at the upper edge
  const rangeMax = Math.min(
    Math.max(Math.ceil((safeCount + 1) / division) * division, division),
    100
  );

  return (
    <GradientRange
      gradient={[
        { color: theme.palette.shades.orange[400], percent: "0%" },
        {
          // TODO: base these %s on benchmark values
          color: theme.palette.shades.meadow[500],
          percent: `${Math.max(3, (10000 / rangeMax) * 0.03)}%`,
        },
      ]}
      marker={count != null ? { value: count } : undefined}
      range={[0, rangeMax]}
    />
  );
};

export const WpmGradientRange = ({
  rate,
  withHighlightedRange = false,
  withMarkerLabel = false,
}: {
  rate: number;
  withHighlightedRange?: boolean;
  withMarkerLabel?: boolean;
}) => {
  const theme = useTheme();

  const labels = withHighlightedRange ? [140, 180, 220, 260] : undefined;
  const highlightedRange: [number, number] | undefined = withHighlightedRange
    ? [COACHING_BENCHMARKS.wpm.min, COACHING_BENCHMARKS.wpm.max]
    : undefined;

  return (
    <GradientRange
      gradient={[
        { color: theme.palette.shades.meadow[500], percent: "0%" },
        { color: theme.palette.shades.meadow[500], percent: "25%" },
        { color: theme.palette.shades.orange[400], percent: "60%" },
        { color: "#FF4242", percent: "100%" },
      ]}
      labels={labels}
      marker={
        rate
          ? { value: rate, label: withMarkerLabel ? `${rate}` : undefined }
          : undefined
      }
      highlightedRange={highlightedRange}
      range={[130, 270]}
    />
  );
};
